import * as React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import theme, { Theme } from 'helpers/theme';
import { createFragmentContainer, graphql } from 'react-relay';
import { Link, Router, useRouter } from 'found';
import PageTitle from 'components/PageTitle';
import Table, { RowCell } from 'components/Table';
import useTableSearch from 'components/Table/useTableSearch';
import {
  BackgroundChecks_backgroundChecks,
  BackgroundChecks_backgroundChecks$data
} from './__generated__/BackgroundChecks_backgroundChecks.graphql';
import UserLabel from 'sharedComponents/core/Table/UserLabel';
import ProfileIcon from 'sharedComponents/core/ProfileIcon';
import moment from 'moment';
import CheckStatus, {
  Status
} from 'features/background-checks/components/CheckStatus';
import { useActionMenuDispatch } from 'components/ActionMenu/ActionMenuProvider';
import FilterOptions from 'features/background-checks/components/FilterOptions';
import {
  getStatus,
  statusText
} from 'features/background-checks/utils/helpers';
import Button from 'components/Button/Button';
import RequestBackgroundCheckLoader from 'features/background-checks/components/RequestBackgroundCheckLoader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlus } from '@fortawesome/pro-regular-svg-icons';

const useStyles = createUseStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: '40px 40px'
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '20px'
  },
  requestButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    whiteSpace: 'nowrap',
    flexShrink: 0
  },
  delegateName: {
    marginLeft: 15
  },
  rowText: {
    fontSize: 13,
    fontWeight: 400,
    textWrap: 'nowrap'
  },
  spacer: {
    marginBottom: 60
  }
}));

export const DEFAULT_LIMIT = 10;

type Props = { backgroundChecks: BackgroundChecks_backgroundChecks };

const BackgroundChecks = ({ backgroundChecks }: Props) => {
  const theme = useTheme();
  const classes = useStyles({ theme });

  const { router, match } = useRouter();
  const action = useActionMenuDispatch();
  const [requestOpen, setRequestOpen] = React.useState(false);

  const formatRow: RowCell<
    BackgroundChecks_backgroundChecks['edges'][number]
  >[] = [
    {
      header: 'Delegate',
      width: '80%',
      format: (data) => {
        // Pending checks are managed on the delegate's pending checks tab;
        // approved checks and verifications go to the delegate's profile tab
        const isPending = !data.approvalDate && !data.isVerification;

        return (
          <Link
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              textDecoration: 'none'
            }}
            to={
              isPending
                ? `/app/delegates/${data.delegate.uuid}?tab=backgroundCheck&check=${data.uuid}`
                : `/app/delegates/${data.delegate.uuid}?tab=profile`
            }
          >
            <ProfileIcon
              name={`${data.delegate.firstName} ${data.delegate.lastName}`}
              url={data.delegate.profileImageUrl ?? undefined}
              size={30}
            />
            <div className={classes.delegateName}>
              <div
                style={{
                  fontWeight: 'bold',
                  fontSize: 14,
                  color: '#1C1C1C'
                }}
              >
                {data.delegate.firstName} {data.delegate.lastName}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: '#6A728F'
                }}
              >
                {data.delegate.email}
              </div>
            </div>
          </Link>
        );
      }
    },
    {
      header: 'Check Progress',
      format: (data) => {
        return <CheckStatus status={getStatus(data)} />;
      }
    },
    {
      header: 'Status',
      format: (data) => {
        return (
          <div className={classes.rowText}>{statusText[getStatus(data)]}</div>
        );
      }
    },
    {
      header: 'Check type',
      format: (data) => {
        return data.isRenewal
          ? 'Renewal'
          : data.isVerification
          ? 'Verification'
          : data.onlyDBS
          ? 'DBS Check'
          : 'Initial';
      }
    },
    {
      header: 'Application Date',
      format: (data) => {
        return (
          <div className={classes.rowText}>
            {moment(data.applicationSentAt ?? undefined).format('DD/MM/YYYY')}
          </div>
        );
      }
    }
  ];

  const pageUrl = (
    page?: { offset: number; limit: number },
    searchQuery?: string | null,
    filter?: {
      approved?: boolean;
      applicationWaiting?: boolean;
      approvalWaiting?: boolean;
      responsesWaiting?: boolean;
    }
  ) => {
    if (!searchQuery) {
      if (searchQuery === '') {
        searchQuery = '';
      } else {
        searchQuery = match.location.query.search ?? '';
      }
    }

    if (!page) {
      if (match.location.query.offset) {
        page = {
          offset: parseInt(match.location.query.offset),
          limit: parseInt(match.location.query.limit)
        };
      }
    }

    if (!filter) {
      filter = {};

      if (match.location.query.approved) {
        filter.approved = match.location.query.approved === 'true';
      }

      if (match.location.query.applicationWaiting) {
        filter.applicationWaiting =
          match.location.query.applicationWaiting === 'true';
      }

      if (match.location.query.approvalWaiting) {
        filter.approvalWaiting =
          match.location.query.approvalWaiting === 'true';
      }

      if (match.location.query.responsesWaiting) {
        filter.responsesWaiting =
          match.location.query.responsesWaiting === 'true';
      }
    }

    let url = `/app/background-checks?`;

    if (page) {
      url += `offset=${page.offset}&limit=${page.limit}&`;
    }

    if (searchQuery) {
      url += `search=${searchQuery}&`;
    }

    if (filter) {
      if (filter.approved) {
        url += `approved=${filter.approved}&`;
      }

      if (filter.applicationWaiting) {
        url += `applicationWaiting=${filter.applicationWaiting}&`;
      }

      if (filter.approvalWaiting) {
        url += `approvalWaiting=${filter.approvalWaiting}&`;
      }

      if (filter.responsesWaiting) {
        url += `responsesWaiting=${filter.responsesWaiting}&`;
      }
    }

    return url;
  };

  const onNextPage = () => {
    let { offset, limit, total } = backgroundChecks.pageInfo;

    if (offset + limit >= total) {
      return;
    }

    router.push(pageUrl({ offset: Math.min(offset + limit, total), limit }));
  };

  const onSearch = useTableSearch((query: string) =>
    pageUrl({ offset: 0, limit: DEFAULT_LIMIT }, query)
  );

  const onPreviousPage = () => {
    let { offset, limit } = backgroundChecks.pageInfo;
    router.push(pageUrl({ offset: Math.max(offset - limit, 0), limit }));
  };

  const onFilterClick = (
    evt: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    action({
      type: 'show',
      position: {
        x: evt.clientX,
        y: evt.clientY,
        placement: 'bottom-left'
      },
      children: (
        <FilterOptions
          state={{
            approved: match.location.query.approved === 'true',
            applicationWaiting:
              match.location.query.applicationWaiting === 'true',
            responsesWaiting: match.location.query.responsesWaiting === 'true',
            approvalWaiting: match.location.query.approvalWaiting === 'true'
          }}
          onChange={(state) => {
            router.push(
              pageUrl({ offset: 0, limit: DEFAULT_LIMIT }, undefined, state)
            );
          }}
        />
      )
    });
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <PageTitle
          title="Background Checks"
          subTitle="Request and track background checks for your delegates"
        />
        <Button
          archetype="flatPrimary"
          className={classes.requestButton}
          onClick={() => setRequestOpen(true)}
        >
          <FontAwesomeIcon icon={faCirclePlus} />
          Request check
        </Button>
      </div>
      {requestOpen && (
        <RequestBackgroundCheckLoader
          onClose={() => setRequestOpen(false)}
          // Reload so the newly requested check appears in the list
          onCompleted={() => window.location.reload()}
        />
      )}
      <div className={classes.spacer} />
      <Table<BackgroundChecks_backgroundChecks$data['edges'][number]>
        data={backgroundChecks.edges}
        formatRow={formatRow}
        onNextPage={onNextPage}
        tableName="background checks"
        onPreviousPage={onPreviousPage}
        pageInfo={backgroundChecks.pageInfo}
        onFilterClick={onFilterClick}
        emptyTableText="No background checks yet - use “Request check” to start one"
        filterActive={
          match.location.query.approved === 'true' ||
          match.location.query.applicationWaiting === 'true' ||
          match.location.query.responsesWaiting === 'true' ||
          match.location.query.approvalWaiting === 'true'
        }
        onSearch={onSearch}
        searchValue={match.location.query.search}
      />
    </div>
  );
};

const BackgroundChecksFrag = createFragmentContainer(BackgroundChecks, {
  backgroundChecks: graphql`
    fragment BackgroundChecks_backgroundChecks on BackgroundCheckPage {
      edges {
        uuid
        delegate {
          uuid
          firstName
          lastName
          email
          profileImageUrl
        }
        approvalDate
        applicationSentAt
        applicationReceivedAt
        hasAllResponses
        requestedDBS
        onlyDBS
        isRenewal
        isVerification
        idExpiryDate
        dbsExpiryDate
      }
      pageInfo {
        total
        offset
        limit
        given
      }
    }
  `
});

export default BackgroundChecksFrag;
